import { PrinterOutlined } from '@ant-design/icons';
import { useApolloClient, useQuery } from '@apollo/client';
import {
    Button,
    DatePicker,
    Drawer,
    Form,
    InputNumber,
    message,
    Select,
    Space,
    Table,
    Tag
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { formatTimeString } from 'utils/dateTimeUtils';
import {
    getStatusColor,
    getStatusText,
    getBagTypeOptions
} from '../graphql/types/ConsolidationTypes';
// eslint-disable-next-line import/no-unresolved
import type { ColumnsType } from 'antd/lib/table';
import { addOrdersToConsolidation } from '../../../../services/consolidationService';
import { getUserInfo } from '../../../../utils/auth';
import ParcelDetailDrawer from '../../../delivery/parcels/components/ParcelDetailDrawer';
import BagDetailDrawer from '../../bags/BagDetailDrawer';
import PrintTagsModal from '../../bags/components/PrintTagsModal';
import { useRemoveFromConsolidation } from '../graphql/mutations/removeFromConsolidation';
import {
    GET_CONSOLIDATION_TASK_DETAIL,
    GetConsolidationTaskDetailResponse,
    GetConsolidationTaskDetailVariables
} from '../graphql/queries/getConsolidationTaskDetail';
import type { CreatedBagInfo } from '../graphql/types/ConsolidationTypes';
import MoveToConsolidationModal from './MoveToConsolidationModal';
import MoveToConsolidationResultModal from './MoveToConsolidationResultModal';
import NewBagModal from './NewBagModal';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';

interface TaskDetailDrawerProps {
    visible: boolean;
    taskId: string | null;
    isOperating?: boolean;
    type: number; // 1: SWIFTX, 2: Other Carrier
    onClose: () => void;
    onEdit?: (taskId: string) => void;
    onDelete?: (taskId: string) => void;
}

const TaskDetailDrawer: React.FC<TaskDetailDrawerProps> = ({
    visible,
    taskId,
    isOperating = false,
    type,
    onClose
}) => {
    const client = useApolloClient();
    const { t } = useTranslation();
    // 新增弹窗及数据状态
    const [newBagModalVisible, setNewBagModalVisible] = useState(false);
    const [newBagsList, setNewBagsList] = useState<CreatedBagInfo[]>([]);
    
    // 移入组包结果弹窗状态
    const [moveResultModalVisible, setMoveResultModalVisible] = useState(false);
    const [moveResults, setMoveResults] = useState<{
        totalCount: number;
        successCount: number;
        failedCount: number;
        failedTrackingNumbers: string[];
    } | null>(null);
    const [pendingNewBags, setPendingNewBags] = useState<CreatedBagInfo[]>([]); // 暂存新包牌数据
    
    // 打印包牌相关状态
    const [printModalVisible, setPrintModalVisible] = useState(false);
    const [selectedTaskBags, setSelectedTaskBags] = useState<any[]>([]);
    const [moveModalVisible, setMoveModalVisible] = useState(false);
    const [removeModalVisible, setRemoveModalVisible] = useState(false);
    // ParcelDetailDrawer 状态管理
    const [detailVisible, setDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');
    const [bagDetailVisible, setBagDetailVisible] = useState(false);
    const [selectedBagNumber, setSelectedBagNumber] = useState<string | null>(null);
    const [bagPage, setBagPage] = useState(1);
    const [bagPageSize, setBagPageSize] = useState(10);
    // 表格展开状态管理
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const { currentWarehouse } = useWarehouseContext();
    
    // 使用真实 GraphQL 查询获取任务详情
    const { data, loading, refetch } = useQuery<GetConsolidationTaskDetailResponse, GetConsolidationTaskDetailVariables>(
        GET_CONSOLIDATION_TASK_DETAIL,
        {
            skip: !visible || !taskId || !currentWarehouse?.id,
            variables: { 
                taskId: taskId!,
                warehouseId: currentWarehouse?.id || '0'
            }
        }
    );
    const taskDetail = data?.getConsolidationTaskDetails || null;
    const [selectedTaskId, setSelectedTaskId] = useState<string>('');
    
    // 移出组包 Mutation
    const [removeFromConsolidation] = useRemoveFromConsolidation();
    
    useEffect(() => {
        if (taskDetail) {
            setSelectedTaskId(taskDetail.taskInfo.taskId);
        }
    }, [taskDetail]);

    // 当 taskId 变化时，重置分页到第一页
    useEffect(() => {
        if (taskId) {
            setBagPage(1);
        }
    }, [taskId]);

    // 当 Drawer 可见性变化时，重置分页
    useEffect(() => {
        if (!visible) {
            setBagPage(1);
            setBagPageSize(10);
        }
    }, [visible]);

    // 处理结果弹窗的OK按钮回调
    const handleShowNewBags = () => {
        if (pendingNewBags.length > 0) {
            setNewBagsList(pendingNewBags);
            setNewBagModalVisible(true);
            setPendingNewBags([]); // 清空暂存数据
        }
    };

    // 处理结果弹窗关闭回调
    const handleResultModalClose = () => {
        setMoveResultModalVisible(false);
        setMoveResults(null);
        // 如果有待显示的新包牌，也触发显示
        if (pendingNewBags.length > 0) {
            handleShowNewBags();
        }
    };
    
    // 打印包牌处理函数
    const handlePrintBags = async () => {
        try {
            if (taskDetail?.bags && taskDetail.bags.length > 0) {
                const bags = taskDetail.bags.map(bag => ({
                    bagId: bag.bagId,
                    bagNumber: bag.bagNumber,
                    bagType: 1, // 默认包牌类型
                    warehouseId: null,
                    sortingCode: bag.bagCode,
                    status: 1,
                    createdAt: null,
                    id: bag.bagId
                }));
                setSelectedTaskBags(bags);
                setPrintModalVisible(true);
            } else {
                message.warning(t('wms.baggingTask.messages.noBagsToPrint'));
            }
        } catch (error) {
            message.error(t('wms.baggingTask.messages.failedToGetBagsInfo'));
        }
    };
    
    // 类型守卫：如果taskDetail为null，显示加载状态
    if (!taskDetail) {
        return (
            <Drawer
                title={t('wms.baggingTask.taskDetailtitle')}
                open={visible}
                onClose={onClose}
                width={type === 2 ? 960 : 720}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    {loading ? t('wms.baggingTask.messages.loading') : t('wms.baggingTask.messages.noData')}
                </div>
            </Drawer>
        );
    }
    
    const tableData = taskDetail.bags.map(bag => ({
        key: `bag-${bag.bagId}`,
        bagId: bag.bagId,
        bagNumber: bag.bagNumber,
        bagCode: bag.bagCode,
        parcelCount: bag.parcelCount,
        handoverVehicle: bag.handoverVehicle,
        handoverAt: bag.handoverAt
    }));
    
    const totalBags = tableData.length;
    const paginatedData = tableData.slice((bagPage - 1) * bagPageSize, bagPage * bagPageSize);
    
    const bagColumns: ColumnsType<any> = [
        {
            title: t('wms.baggingTask.filters.bagNumber'),
            dataIndex: 'bagNumber',
            key: 'bagNumber',
            width: 200,
            render: (text: string) => (
                <Button
                    type="link"
                    onClick={() => {
                        setSelectedBagNumber(text);
                        setBagDetailVisible(true);
                    }}
                    style={{ color: '#722ed1', padding: 0 }}
                >
                    {text}
                </Button>
            )
        },
        {
            title: t('wms.baggingTask.filters.sortingCode'),
            dataIndex: 'bagCode',
            key: 'bagCode',
            width: 120
        },
        ...(type === 2 ? [
            {
                title: t('wms.baggingTask.columns.handoverVehicle'),
                dataIndex: 'handoverVehicle',
                key: 'handoverVehicle',
                width: 150,
                render: (text: string, record: any) => {
                    // 只有父行显示车牌，子行不显示
                    return record.isParent ? (text || '-') : null;
                }
            },
            {
                title: t('wms.baggingTask.columns.handoverTime'),
                dataIndex: 'handoverAt',
                key: 'handoverAt',
                width: 160,
                render: (time: string, record: any) => {
                    // 只有父行显示交接时间，子行不显示
                    return record.isParent ? (time ? formatTimeString(time).toString() : '-') : null;
                }
            }
        ] : []),
        {
            title: t('wms.baggingTask.filters.content'),
            dataIndex: 'parcelCount',
            key: 'parcelCount',
            width: 230
        }
    ];
    return (
        <Drawer
            title={
                <div className="flex items-center justify-between">
                        <span>{t('wms.baggingTask.filters.taskDetailtitle')} - {taskDetail.taskInfo.taskName}</span>
                </div>
            }
            placement="right"
            open={visible}
            onClose={onClose}
            width={type === 2 ? 960 : 720}
            footer={
                <Space style={{ float: 'right' }}>
                    <Button 
                        onClick={handlePrintBags}
                        disabled={!taskDetail?.bags || taskDetail.bags.length === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.baggingTask.buttons.print')}</span>
                    </Button>
                    <Button 
                        onClick={() => { setSelectedTaskId(taskDetail.taskInfo.taskId); setMoveModalVisible(true); }}
                        disabled={isOperating}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('wms.baggingTask.buttons.moveIntoBag')}</span>
                    </Button>
                    <Button 
                        type="primary" 
                        onClick={onClose}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('components.button.ok')}</span>
                    </Button>
                </Space>
            }
            loading={loading}
        >
            <div
            className="space-y-6"
            style={{
                paddingRight: '16px',
                minWidth: type === 2 ? '900px' : '560px',
                maxWidth: type === 2 ? '900px' : '560px', // 👈 根据类型调整内容最大宽度
                overflow: 'hidden' // 👈 新增，防止关闭动画内容外溢
            }}
>
                {/* 基本信息 */}
                <Form layout="vertical">
                    {type === 2 && (
                        <div className="flex items-center gap-3 mb-4">
                            <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.columns.carrier')}</label>
                            <Select
                                disabled
                                value={(taskDetail as any)?.taskInfo?.carrier || (taskDetail as any)?.taskInfo?.carrierCode || '-'}
                                className="flex-1"
                            >
                                <Select.Option value={(taskDetail as any)?.taskInfo?.carrier || (taskDetail as any)?.taskInfo?.carrierCode || '-' }>
                                    {(taskDetail as any)?.taskInfo?.carrier || (taskDetail as any)?.taskInfo?.carrierCode || '-'}
                                </Select.Option>
                            </Select>
                        </div>
                    )}
                    <div className="flex items-center gap-3 mb-4">
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t(type === 2 ? 'wms.baggingTask.filters.handoverHub' : 'wms.baggingTask.filters.destinationHub')}</label>
                        <Select
                            disabled 
                            value={taskDetail.taskInfo.destinationHubId}
                            className="flex-1"
                        >
                            <Select.Option value={taskDetail.taskInfo.destinationHubId}>
                                {taskDetail.taskInfo.destinationHubName}
                            </Select.Option>
                        </Select>
                    </div>
                    
                    {type === 1 && (
                        <div className="flex items-center gap-3 mb-4">
                            <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.destinationWHS')}</label>
                            <Select
                                disabled
                                value={taskDetail.taskInfo.destinationWhsId}
                                className="flex-1"
                            >
                                <Select.Option value={taskDetail.taskInfo.destinationWhsId}>
                                    {taskDetail.taskInfo.destinationWhsName}
                                </Select.Option>
                            </Select>
                        </div>
                    )}
                    
                    <div className="flex items-center gap-3 mb-4">
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.arrivalTime')}</label>
                        <DatePicker.RangePicker
                            disabled
                            showTime
                            format="YYYY/MM/DD HH:mm"
                            value={[
                                taskDetail.taskInfo.startTime ? dayjs(taskDetail.taskInfo.startTime) : null,
                                taskDetail.taskInfo.endTime ? dayjs(taskDetail.taskInfo.endTime) : null
                            ]}
                            className="flex-1"
                        />
                    </div>
                    
                    {type === 1 && (
                        <div className="flex items-center gap-3 mb-4">
                            <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.bagType')}</label>
                            <Select
                                disabled
                                value={taskDetail.taskInfo.bagType}
                                className="flex-1"
                            >
                                {getBagTypeOptions().map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}
                    <div className="flex items-center gap-3 mb-4">
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.parcelCount')}</label>
                        <InputNumber
                            disabled
                            value={taskDetail.statistics.totalParcelCount}
                            className="flex-1"
                        />
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20 ml-4">{t('wms.baggingTask.filters.bagCount')}</label>
                        <InputNumber
                            disabled
                            value={taskDetail.statistics.totalBagCount}
                            className="flex-1"
                        />
                        <Button 
                            type="primary" 
                            onClick={() => refetch()}
                            className="ml-auto bg-purple-500 hover:bg-purple-600 border-purple-500"
                            title="刷新页面"
                        >
                            {t('components.button.update')}
                        </Button>
                    </div>
                    
                    <div className="flex items-center gap-3 mb-4">
                        <label className="text-sm text-gray-600 flex-shrink-0 min-w-20">{t('wms.baggingTask.filters.taskStatus')}</label>
                        <Tag color={getStatusColor(taskDetail.taskInfo.status)}>
                            {getStatusText(taskDetail.taskInfo.status)}
                        </Tag>
                    </div>
                </Form>
                {/* 包牌列表 */}
                <div className="overflow-x-auto" style={{ 
                    width: '100%', 
                    maxWidth: '100%',
                    minWidth: type === 2 ? '900px' : '560px'
                }}>
                    <Table
                        columns={bagColumns}
                        dataSource={paginatedData}
                        pagination={{
                            current: bagPage,
                            pageSize: bagPageSize,
                            total: totalBags,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '15', '20'],
                            onChange: (page, size) => {
                                setBagPage(page);
                                if (size && size !== bagPageSize) {
                                    setBagPageSize(size);
                                    setBagPage(1);
                                }
                            },
                        }}
                        size="small"
                        rowKey="key"
                        scroll={{ x: 'max-content' }}
                        style={{
                            width: '100%',
                            tableLayout: 'fixed'
                        }}
                        loading={loading}
                    />
                </div>
            </div>
            {/* 新包牌弹窗 */}
            <NewBagModal
                visible={newBagModalVisible}
                newBags={newBagsList}
                onPrint={() => {
                    if (newBagsList.length > 0) {
                        const bags = newBagsList.map(bag => ({
                            bagId: bag.bagId,
                            bagNumber: bag.bagNumber,
                            id: bag.bagId,
                            bagType: 1,
                            warehouseId: null,
                            sortingCode: bag.bagCode,
                            status: 1,
                            createdAt: null,
                        }));
                        setSelectedTaskBags(bags);
                        setPrintModalVisible(true);
                        setNewBagModalVisible(false);
                    } else {
                        message.warning('暂无新包牌可打印');
                    }
                }}
                onOk={() => setNewBagModalVisible(false)}
            />
            {/* 移入组包输入弹窗 */}
            <MoveToConsolidationModal
              visible={moveModalVisible}
              defaultTaskId={selectedTaskId}
              type={type}
              onOk={async (taskId, trackingNumbers) => {
                try {
                  const response = await addOrdersToConsolidation(currentWarehouse?.id!, taskId, trackingNumbers);
                  const { newBags, results, message: msg } = response;
                  
                  // 1. 关闭输入弹窗
                  setMoveModalVisible(false);
                  
                  // 2. 暂存新包牌数据，稍后显示
                  setPendingNewBags(newBags);
                  
                  // 3. 显示处理结果统计弹窗
                  setMoveResults(results);
                  setMoveResultModalVisible(true);
                  
                  // // 4. 根据结果显示相应的全局提示
                  // if (results.failedCount > 0) {
                  //   message.warning(`Processing completed: ${results.successCount} successful, ${results.failedCount} failed`);
                  // } else {
                  //   message.success(`Move to consolidation completed: ${results.successCount} orders processed successfully`);
                  // }
                  
                  // 5. 刷新数据
                  refetch();
                } catch (error) {
                  message.error(t('wms.baggingTask.messages.failedToMoveToConsolidation'));
                  console.error('Move to consolidation error:', error);
                }
              }}
              onCancel={() => setMoveModalVisible(false)}
            />
            {/*/!* 移出组包弹窗 *!/*/}
            {/*<RemoveFromConsolidationModal*/}
            {/*  visible={removeModalVisible}*/}
            {/*  onOk={async (trackingNumbers) => {*/}
            {/*    setRemoveModalVisible(false);*/}
            {/*    try {*/}
            {/*      const { data } = await removeFromConsolidation({*/}
            {/*        variables: {*/}
            {/*          input: {*/}
            {/*            trackingNumbers*/}
            {/*          }*/}
            {/*        }*/}
            {/*      });*/}

            {/*      if (data?.removeOrdersFromConsolidation.success) {*/}
            {/*        message.success(data.removeOrdersFromConsolidation.message);*/}
            {/*        // 刷新任务详情*/}
            {/*        window.location.reload();*/}
            {/*      } else {*/}
            {/*        message.error(data?.removeOrdersFromConsolidation.message || '移出组包失败');*/}
            {/*      }*/}
            {/*    } catch (error: any) {*/}
            {/*      message.error('移出组包失败: ' + (error.message || '未知错误'));*/}
            {/*    }*/}
            {/*  }}*/}
            {/*  onCancel={() => setRemoveModalVisible(false)}*/}
            {/*/>*/}
            {/* 包裹详情弹窗 */}
            {detailVisible && selectedOrderId && (
                <ParcelDetailDrawer
                    visible={detailVisible}
                    orderId={selectedOrderId}
                    onClose={() => setDetailVisible(false)}
                    userRole={getUserInfo()?.account?.role?.name || ''}
                />
            )}
            {/* 包牌详情弹窗 */}
             <BagDetailDrawer
                 visible={bagDetailVisible}
                 bagNumber={selectedBagNumber}
                 onClose={() => {
                     setBagDetailVisible(false);
                     setSelectedBagNumber(null);
                 }}
             />
             
             {/* 移入组包结果弹窗 */}
             <MoveToConsolidationResultModal
                 visible={moveResultModalVisible}
                 results={moveResults}
                 onClose={handleResultModalClose}
                 onShowNewBags={handleShowNewBags}
             />

             {/* 打印包牌弹窗 */}
             <PrintTagsModal
                 visible={printModalVisible}
                 onClose={() => setPrintModalVisible(false)}
                 bags={selectedTaskBags}
                 initialPrintCopies={1}
             />
        </Drawer>
    );
};

export default TaskDetailDrawer;